import { useRef, useState } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Divider, Flex, Icon, Text } from '@nula/core-ui';
import type { SurfaceEntry, SurfaceSource } from '@/lib/sync/document-surfaces';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import { useRovingFocus } from '../hooks/use-roving-focus';
import { SurfaceRow } from './surface-row';
import { SurfaceRowControls } from './surface-row-controls';
import { SurfaceDetailFace, type SurfaceRegenerateOffer } from './surface-detail-face';
import { SurfaceNewFace } from './surface-new-face';
import { SurfaceTemplatesFace } from './surface-templates-face';
import { SurfaceTemplateFace } from './surface-template-face';
import { SurfacePendingRow, type PendingSurfaceRow } from './surface-pending-row';
import type { DocumentSourceFacts, TemplateSummary } from '@/lib/templates/catalog';
import type { TemplateSourceOption } from '../utils/template-sources';
import { SURFACE_CAP } from '@/lib/sync/create-surface';
import { isAtSurfaceCap } from '../utils/surface-cap';
import styles from './surface-panel.module.css';

interface SurfacePanelProps {
  bodyTitle: string;
  entries: readonly SurfaceEntry[];
  activeId: string | null;
  onSelect: (id: string | null) => void;
  /** Switches to the New face. Absent at the cap — the New row is a statement instead. */
  onNew?: () => void;
  /** Which face the panel shows. Defaults to the surfaces list. */
  face?: 'surfaces' | 'new' | 'detail' | 'templates' | 'template';
  /** The surface the detail face acts on, when `face` is 'detail'. */
  detailId?: string | null;
  /** Opens a surface's detail face from its row. */
  onOpenDetail?: (id: string) => void;
  /** Reports the kind chosen on the New face. */
  onCreate?: (surface: 'text' | 'canvas') => void;
  /** Returns to the surfaces face without creating. */
  onBack?: () => void;
  /** The id of the row currently in inline-rename mode, or `null`. */
  editingId?: string | null;
  onRenameStart?: (id: string) => void;
  onRenameCommit?: (title: string) => void;
  onRenameCancel?: () => void;
  onMove?: (id: string, direction: 'up' | 'down') => void;
  onDelete?: (id: string) => void;
  /**
   * Whether the viewer can mutate surfaces (create/rename/move/delete).
   * Defaults to true. A viewer who cannot edit still sees and can switch
   * between every surface — reading is not an edit (ui-design.md §9.6) — only
   * the mutating affordances (the New row, each row's options menu) hide.
   */
  canEdit?: boolean;
  /** Forwarded to the New face — fires when Audio is chosen. */
  onRecord?: () => void;
  /** Forwarded to the New face — whether the document can still record. */
  canRecord?: boolean;
  /** The id of the surface currently being recorded into, or null. That row
   *  shows the shared recording dot and hides its options menu (§5.2). */
  liveSurfaceId?: string | null;
  /** The template catalogue. With `onOpenTemplates`, the New face offers the Template row. */
  templates?: readonly TemplateSummary[];
  templateFacts?: DocumentSourceFacts;
  /** Switches to the Templates face. Absent means the New face has no template row. */
  onOpenTemplates?: () => void;
  templateId?: string | null;
  onPickTemplate?: (templateId: string) => void;
  sources?: readonly TemplateSourceOption[];
  initialSources?: readonly SurfaceSource[] | null;
  templateRefusal?: string | null;
  templateBusy?: boolean;
  templatePending?: boolean;
  onGenerate?: (templateId: string, sources: TemplateSourceOption[]) => void;
  pendingRows?: readonly PendingSurfaceRow[];
  onRemovePending?: (runId: string) => void;
  /** Regenerate or Review on the detail face; absent when neither is offered. */
  detailRegenerate?: SurfaceRegenerateOffer;
}

/** Re-exported so the panel's consumers keep their import path; the cap
 *  itself belongs beside the function that enforces it. */
export { SURFACE_CAP };

const NO_SOURCE_OPTIONS: readonly TemplateSourceOption[] = [];

const NO_SOURCES: DocumentSourceFacts = { hasBodyText: false, hasSurfaceText: false, finishedRecordingIds: [] };

const panel = cva(styles.panel);

const bodyRow = cva(styles.bodyRow, {
  variants: {
    active: { true: styles.bodyRowActive },
  },
});

/**
 * The surfaces panel: the pinned body row and the scrolling surfaces list,
 * then a divider, then the New row, then the header — matching ui-design.md
 * §4's bottom-anchored layout (the panel grows upward from the orb, so New
 * is the floor nearest the cursor). The body row and New row are outside the
 * scroller (§2, §3.1) so neither can scroll away. The body row paints at the
 * top of the list (surfaces run oldest-to-newest downward); it is already
 * first in DOM, so screen-reader/tab order and visual order agree with no
 * inversion anywhere.
 */
export function SurfacePanel({
  bodyTitle, entries, activeId, onSelect, onNew, face = 'surfaces', onCreate, onBack,
  detailId = null, onOpenDetail,
  editingId = null, onRenameStart, onRenameCommit, onRenameCancel, onMove, onDelete,
  canEdit = true, onRecord, canRecord = false, liveSurfaceId = null,
  templates = [], templateFacts = NO_SOURCES, onOpenTemplates, templateId = null, onPickTemplate,
  sources = NO_SOURCE_OPTIONS, initialSources = null, templateRefusal = null, templateBusy = false, templatePending = false, onGenerate,
  pendingRows = [], onRemovePending, detailRegenerate,
}: SurfacePanelProps) {
  const atCap = isAtSurfaceCap(entries.length, pendingRows);
  const [templateQuery, setTemplateQuery] = useState('');
  const [templateHighlightKey, setTemplateHighlightKey] = useState<string | null>(null);
  if (face !== 'templates' && face !== 'template'
    && (templateQuery !== '' || templateHighlightKey !== null)) {
    setTemplateQuery('');
    setTemplateHighlightKey(null);
  }
  const [scrolled, setScrolled] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { onKeyDown: onRovingKeyDown } = useRovingFocus('[role="menuitemradio"],[role="menuitem"]');

  const handleScroll = () => {
    setScrolled((scrollRef.current?.scrollTop ?? 0) > 0);
  };

  const detailEntry = face === 'detail' ? entries.find((e) => e.id === detailId) : undefined;
  const template = face === 'template' ? templates.find((t) => t.id === templateId) : undefined;

  // A detail face whose surface has vanished (a peer deleted it mid-look) falls
  // back to the list rather than rendering an empty face.
  if (detailEntry) {
    return (
      <Flex
        vertical
        role="menu"
        aria-label="Surfaces"
        className={panel()}
        onKeyDown={onRovingKeyDown}
      >
        <SurfaceDetailFace
          entry={detailEntry}
          onBack={() => onBack?.()}
          onRename={() => onRenameStart?.(detailEntry.id)}
          onDelete={() => onDelete?.(detailEntry.id)}
          regenerate={detailRegenerate}
        />
      </Flex>
    );
  }

  if (template) {
    return (
      <Flex
        vertical
        role="menu"
        aria-label="Surfaces"
        className={panel()}
        onKeyDown={onRovingKeyDown}
      >
        <SurfaceTemplateFace
          key={template.id}
          template={template}
          sources={sources}
          initialSources={initialSources}
          refusal={templateRefusal}
          busy={templateBusy}
          pending={templatePending}
          onGenerate={(ticked) => onGenerate?.(template.id, ticked)}
          onBack={() => onOpenTemplates?.()}
        />
      </Flex>
    );
  }

  if (face === 'templates' || face === 'template') {
    return (
      <Flex
        vertical
        role="menu"
        aria-label="Surfaces"
        className={panel()}
        onKeyDown={onRovingKeyDown}
      >
        <SurfaceTemplatesFace
          templates={templates}
          facts={templateFacts}
          query={templateQuery}
          onQueryChange={(query) => {
            setTemplateQuery(query);
            setTemplateHighlightKey(null);
          }}
          initialHighlightKey={templateHighlightKey}
          onPickRow={setTemplateHighlightKey}
          onPick={(id) => onPickTemplate?.(id)}
          onBack={() => onNew?.()}
        />
      </Flex>
    );
  }

  if (face === 'new') {
    return (
      <Flex
        vertical
        role="menu"
        aria-label="Surfaces"
        className={panel()}
        onKeyDown={onRovingKeyDown}
      >
        <SurfaceNewFace
          onCreate={(surface) => onCreate?.(surface)}
          onBack={() => onBack?.()}
          showBack={entries.length > 0}
          onRecord={onRecord}
          canRecord={canRecord}
          onTemplates={onOpenTemplates}
          templateCount={templates.length}
        />
      </Flex>
    );
  }

  return (
    <Flex
      vertical
      role="menu"
      aria-label="Surfaces"
      className={panel()}
      onKeyDown={onRovingKeyDown}
    >
      <Flex vertical className={styles.rows}>
        <button
          type="button"
          role="menuitemradio"
          aria-checked={activeId === null}
          tabIndex={activeId === null ? 0 : -1}
          onClick={() => onSelect(null)}
          className={bodyRow({ active: activeId === null })}
        >
          <Flex
            component="span"
            className={clsx(styles.bodyRail, activeId === null && styles.bodyRailActive)}
          />
          {/* The body is the document's own text surface, so it carries the
              same kind glyph a text surface row does — without it the row read
              as a different kind of thing rather than the first surface. */}
          <Icon name={getEntityKindMeta('document').icon} size={18} className={styles.bodyIcon} />
          <Text className={styles.bodyName}>{bodyTitle}</Text>
        </button>

        <Flex vertical className={clsx(styles.wrap, scrolled && styles.scrolling)}>
          <Flex vertical ref={scrollRef} onScroll={handleScroll} className={styles.scroll}>
            {entries.map((entry, index) => (
              <SurfaceRow
                key={entry.id}
                entry={entry}
                active={activeId === entry.id}
                tabIndex={activeId === entry.id ? 0 : -1}
                onSelect={onSelect}
                editing={editingId === entry.id}
                onRenameCommit={onRenameCommit}
                onRenameCancel={onRenameCancel}
                live={liveSurfaceId === entry.id}
                trailing={canEdit ? (
                  <SurfaceRowControls
                    entry={entry}
                    isFirst={index === 0}
                    isLast={index === entries.length - 1}
                    onMove={(direction) => onMove?.(entry.id, direction)}
                    onOpenDetail={() => onOpenDetail?.(entry.id)}
                  />
                ) : undefined}
              />
            ))}
            {pendingRows.map((row) => (
              <SurfacePendingRow key={row.runId} row={row} onRemove={(id) => onRemovePending?.(id)} />
            ))}
          </Flex>
        </Flex>
      </Flex>

      {canEdit && <Divider role="separator" className={styles.divider} />}

      {canEdit && (atCap ? (
        <Flex align="center" className={clsx(styles.newRow, styles.newRowCapped)} role="presentation">
          <Icon name="warning" size={18} />
          <Text className={styles.bodyName}>{SURFACE_CAP} of {SURFACE_CAP} added</Text>
        </Flex>
      ) : (
        <button type="button" role="menuitem" className={styles.newRow} onClick={onNew}>
          <Icon name="add" size={18} />
          <Text className={styles.bodyName}>New</Text>
        </button>
      ))}

    </Flex>
  );
}
